import { useTranslation } from "react-i18next"

import { Check } from "@/components/icons"
import { cn } from "@/lib/utils"

type TrustedSourceItem = {
  id: string
  name: string
  url: string
}

type TrustedSourcesPanelProps = {
  /** Whether any sources exist at all (drives the empty state). */
  hasSources: boolean
  personalSources: TrustedSourceItem[]
  companySources: TrustedSourceItem[]
  selectedUrls: string[]
  isAllSelected: boolean
  toggleSource: (url: string, checked: boolean) => void
  toggleAll: () => void
  trustedMode: "only" | "preferred"
  setTrustedMode: (mode: "only" | "preferred") => void
  /** Open the full management dialog (caller also closes its own surface). */
  onManage: () => void
}

/**
 * Flat, multi-select panel for trusted sources. Renders Personal/Company
 * sections, a select-all toggle, and the mode radio group as plain buttons so
 * it can live inside a stays-open container (a Popover/DropdownMenuSub on
 * desktop, a Drawer on mobile) — selecting a source never dismisses the
 * surface, and there is no submenu tree to re-traverse between picks.
 */
export function TrustedSourcesPanel({
  hasSources,
  personalSources,
  companySources,
  selectedUrls,
  isAllSelected,
  toggleSource,
  toggleAll,
  trustedMode,
  setTrustedMode,
  onManage,
}: TrustedSourcesPanelProps) {
  const { t } = useTranslation()

  const renderSourceButton = (source: TrustedSourceItem) => {
    const checked = selectedUrls.includes(source.url)
    return (
      <button
        key={source.id}
        aria-checked={checked}
        className="hover:bg-accent flex w-full items-center gap-2 px-4 py-2 text-left text-sm"
        role="checkbox"
        type="button"
        onClick={() => toggleSource(source.url, !checked)}
      >
        {/* Presentational checkbox marker (matches ui/checkbox styling) — the
            row button owns the click + role, so we must not nest a real
            interactive Checkbox (itself a <button>) inside this <button>. */}
        <span
          aria-hidden
          className={cn(
            "border-primary flex size-4 shrink-0 items-center justify-center rounded-[4px] border shadow",
            checked && "bg-primary text-primary-foreground"
          )}
        >
          {checked && <Check className="size-3.5" strokeWidth={2} />}
        </span>
        <span className="truncate">{source.name}</span>
      </button>
    )
  }

  return (
    <>
      {!hasSources && (
        <div className="text-muted-foreground px-4 py-2 text-sm">
          {t("sources.no_sources_available")}
        </div>
      )}
      {hasSources && (
        <>
          <button
            className="hover:bg-accent w-full px-4 py-2 text-left text-sm"
            type="button"
            onClick={() => toggleAll()}
          >
            {isAllSelected ? t("sources.disable_all_trusted") : t("sources.enable_all_trusted")}
          </button>
          {personalSources.length > 0 && (
            <>
              <div className="text-muted-foreground px-4 pt-3 pb-1 text-xs font-medium uppercase">
                {t("sources.personal_sources")}
              </div>
              {personalSources.map(renderSourceButton)}
            </>
          )}
          {companySources.length > 0 && (
            <>
              <div className="text-muted-foreground px-4 pt-3 pb-1 text-xs font-medium uppercase">
                {t("sources.company_sources")}
              </div>
              {companySources.map(renderSourceButton)}
            </>
          )}
          <div className="text-muted-foreground px-4 pt-3 pb-1 text-xs font-medium uppercase">
            {t("sources.mode")}
          </div>
          <div aria-label={t("sources.mode")} role="radiogroup">
            {(["only", "preferred"] as const).map((modeKey) => {
              const labelKey =
                modeKey === "only" ? "sources.trusted_only" : "sources.trusted_preferred"
              const checked = trustedMode === modeKey
              return (
                <button
                  key={modeKey}
                  aria-checked={checked}
                  className="hover:bg-accent flex w-full items-center gap-2 px-4 py-2 text-left text-sm"
                  role="radio"
                  type="button"
                  onClick={() => setTrustedMode(modeKey)}
                >
                  <span
                    className={cn(
                      "size-4 shrink-0 rounded-full border",
                      checked ? "border-primary bg-primary" : "border-muted-foreground/40"
                    )}
                  />
                  <span className="truncate">{t(labelKey)}</span>
                </button>
              )
            })}
          </div>
        </>
      )}
      <div className="border-border mt-2 border-t px-2 pt-2">
        <button
          className="hover:bg-accent w-full rounded px-2 py-2 text-left text-sm"
          type="button"
          onClick={onManage}
        >
          {t("sources.manage")}
        </button>
      </div>
    </>
  )
}
